import { differenceInDays } from 'date-fns';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Button, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Column, Divider, Row } from '~/commonComponents';
import { JobPostingStatus, useGetOfficePlanDetail } from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { WarningSolidIcon } from '~/images/icons';
import { routes } from '~/routes';

interface ISuspendedBannerProps {
  jobStatus: JobPostingStatus;
  officeId?: string;
  officePlanId?: string;
}

export const SuspendedBanner = ({ jobStatus, officeId, officePlanId }: ISuspendedBannerProps) => {
  const navigate = useNavigate();

  // Fetch office plan details to get grace period information
  const {
    data: officePlanData,
    isError,
    error,
  } = useGetOfficePlanDetail(officePlanId, officeId, {
    enabled: !!officePlanId && jobStatus === JobPostingStatus.Suspended,
  });

  useEffect(() => {
    if (isError) {
      showErrorMessage({ error });
    }
  }, [isError, error]);

  // Calculate remaining days until grace period ends
  const remainingDays = officePlanData?.gracePeriodEndsAt
    ? differenceInDays(new Date(officePlanData.gracePeriodEndsAt), new Date())
    : null;

  if (jobStatus !== JobPostingStatus.Suspended) {
    return null;
  }

  return (
    <Row
      alignItems="center"
      backgroundColor={colors.warning[30]}
      borderColor={colors.warning[700]}
      borderRadius={6}
      borderStyle="solid"
      borderWidth={1}
      gap={12}
      marginTop={20}
      paddingBlock={12}
      paddingInline={10}
    >
      <WarningSolidIcon color={colors.warning[500]} size={24} />
      <Divider height="95%" width={1} color={colors.warning[500]} />
      <Row flex={1}>
        <Column>
          <Typography as="span" bold color={colors.warning[700]} style={{ marginBottom: 4 }}>
            This Job Posting Is Suspended:
          </Typography>
          <Typography as="span" color={colors.warning[700]}>
            Candidates can no longer view or apply to this job. Onsite interviews within{' '}
            {officePlanData?.gracePeriodDays ?? '-'} days of the suspension date will remain active.
            All others have been canceled. You can reactivate this posting by updating your payment
            method within the next {remainingDays ?? '-'} days before it&apos;s permanently closed.
          </Typography>
        </Column>
        <Column flex={1} alignSelf="center" alignItems="center">
          <Button
            variant="warning"
            size="xs"
            style={{ marginLeft: 32, marginRight: 48 }}
            onClick={() => navigate(routes.paymentInformation)}
          >
            Update Payment
          </Button>
        </Column>
      </Row>
    </Row>
  );
};
